import { useState, useEffect } from "react";
import { ChevronRight, Plus, Zap, Headphones, RefreshCw, Lock } from "lucide-react";
import { supabase } from "../supabase";

const PLAN_FEATURES = {
  nano:    { sales: true,  support: false, relance: false, bank: false },
  starter: { sales: true,  support: true,  relance: false, bank: false },
  pro:     { sales: true,  support: true,  relance: true,  bank: false },
  premium: { sales: true,  support: true,  relance: true,  bank: false },
  trial:   { sales: true,  support: false, relance: false, bank: false },
};

function SettingRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
      <span className="text-sm text-gray-800">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400">{value || '—'}</span>
        <ChevronRight size={14} className="text-gray-300" />
      </div>
    </div>
  );
}

function ModeCard({ icon: Icon, title, description, available, active, locked, onToggle, upgradeText }) {
  return (
    <div className={`border rounded-xl p-4 transition-all ${
      locked ? 'border-gray-100 bg-gray-50 opacity-60' :
      active ? 'border-orange-300 bg-orange-50' :
      'border-gray-100 bg-white hover:border-gray-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            locked ? 'bg-gray-100' :
            active ? 'bg-orange-100' : 'bg-gray-100'
          }`}>
            {locked
              ? <Lock size={16} className="text-gray-400" />
              : <Icon size={16} className={active ? 'text-orange-600' : 'text-gray-500'} />
            }
          </div>
          <div>
            <div className="text-sm font-medium text-gray-800">{title}</div>
            <div className="text-xs text-gray-500 mt-0.5">{description}</div>
            {locked && upgradeText && (
              <div className="text-xs text-orange-600 mt-1 font-medium">{upgradeText}</div>
            )}
          </div>
        </div>
        {!locked && available && (
          <button
            onClick={onToggle}
            className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
              active ? 'bg-orange-500' : 'bg-gray-200'
            }`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              active ? 'translate-x-5' : 'translate-x-0.5'
            }`}/>
          </button>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState("assistant");
  const [org, setOrg] = useState(null);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Mode Hellodia
  const [salesMode, setSalesMode] = useState(true);
  const [supportMode, setSupportMode] = useState(false);
  const [relanceMode, setRelanceMode] = useState(false);
  const [relanceType, setRelanceType] = useState('manual');
  const [relanceDelay, setRelanceDelay] = useState(3);

  useEffect(() => {
    async function fetchOrgId() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id')
        .eq('user_id', user.id)
        .single();
      setOrgId(orgData?.id || null);
    }
    fetchOrgId();
  }, []);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    async function fetchData() {
      try {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', orgId)
          .single();

        const { data: productsData } = await supabase
          .from('products')
          .select('*')
          .eq('organization_id', orgId)
          .eq('is_active', true);

        const { data: paymentData } = await supabase
          .from('payment_methods')
          .select('*')
          .eq('organization_id', orgId)
          .eq('is_active', true);

        setOrg(orgData);
        setProducts(productsData || []);
        setPaymentMethods(paymentData || []);

        // Initialiser les modes depuis la DB
        const mode = orgData?.business_mode || 'sales';
        setSalesMode(true);
        setSupportMode(mode === 'support' || mode === 'both');
        setRelanceMode(orgData?.relance_mode !== 'none');
        setRelanceType(orgData?.relance_mode || 'manual');
        setRelanceDelay(orgData?.relance_delay_days || 3);

      } catch (error) {
        console.error('Erreur settings:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [orgId]);

  const planName = org?.is_trial ? 'trial' : (org?.plan_name || org?.subscription_plan || 'starter');
  const features = PLAN_FEATURES[planName] || PLAN_FEATURES.starter;

  async function saveMode() {
    if (!orgId) return;
    setSaving(true);
    try {
      let business_mode = 'sales';
      if (supportMode && salesMode) business_mode = 'both';
      else if (supportMode) business_mode = 'support';
      else business_mode = 'sales';

      await fetch(`https://smart-crm-production.up.railway.app/api/settings/mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId,
          business_mode,
          relance_mode: relanceMode ? relanceType : 'none',
          relance_delay_days: relanceDelay
        })
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Erreur saveMode:', error);
    } finally {
      setSaving(false);
    }
  }

  const upgradeText = (needed) => {
    const upgrades = { starter: 'Starter 59€/mois', pro: 'Pro 119€/mois', premium: 'Premium' };
    return `Disponible dès le plan ${upgrades[needed]}`;
  };

  const tabs = [
    { id: "assistant", label: "Mon assistant" },
    { id: "mode", label: "Mode Hellodia" },
    { id: "products", label: "Produits" },
    { id: "subscription", label: "Abonnement" },
  ];

  if (loading) {
    return <div className="text-center py-10 text-gray-400 text-sm">Chargement...</div>;
  }

  return (
    <div className="max-w-2xl space-y-4">

      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              activeTab === tab.id
                ? "bg-orange-100 text-orange-800 border-orange-300"
                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── ASSISTANT ── */}
      {activeTab === "assistant" && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-800">Configuration de l'assistant</span>
          </div>
          {org?.is_trial && (
            <div className="px-4 py-2 bg-orange-50 border-b border-orange-100">
              <p className="text-xs text-orange-700">
                🎁 Période d'essai gratuite — Envoyez <strong>START</strong> sur WhatsApp pour configurer votre assistant.
              </p>
            </div>
          )}
          <SettingRow label="Nom de l'agent" value={org?.agent_name} />
          <SettingRow label="Nom du business" value={org?.name} />
          <SettingRow label="Type d'activité" value={org?.business_type} />
          <SettingRow label="Numéro WhatsApp" value={org?.phone} />
          <SettingRow label="Plan actuel" value={org?.is_trial ? 'Trial 24h' : org?.subscription_plan} />
          {paymentMethods.map((pm, i) => (
            <SettingRow key={i} label={pm.method_name} value={`${pm.account_number} · ${pm.account_name}`} />
          ))}
        </div>
      )}

      {/* ── MODE HELLODIA ── */}
      {activeTab === "mode" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="text-sm font-medium text-gray-800">Fonctionnalités actives</div>
              <div className="text-xs text-gray-400 mt-0.5">
                Plan actuel : <span className="font-medium text-orange-600 capitalize">{planName}</span>
              </div>
            </div>
            <div className="p-4 space-y-3">

              {/* Vente — toujours actif */}
              <ModeCard
                icon={Zap}
                title="Vente"
                description="Hellodia répond, convainc et convertit vos prospects automatiquement."
                available={true}
                active={true}
                locked={false}
                onToggle={() => {}}
              />

              {/* Support */}
              <ModeCard
                icon={Headphones}
                title="Support client"
                description="Hellodia gère les tickets, réclamations et demandes SAV."
                available={features.support}
                active={features.support && supportMode}
                locked={!features.support}
                upgradeText={!features.support ? upgradeText('starter') : null}
                onToggle={() => features.support && setSupportMode(!supportMode)}
              />

              {/* Relances */}
              <ModeCard
                icon={RefreshCw}
                title="Relances automatiques"
                description="Hellodia détecte et relance les conversations abandonnées."
                available={features.relance}
                active={features.relance && relanceMode}
                locked={!features.relance}
                upgradeText={!features.relance ? upgradeText('pro') : null}
                onToggle={() => features.relance && setRelanceMode(!relanceMode)}
              />

            </div>
          </div>

          {/* Config relances si activé */}
          {features.relance && relanceMode && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-800">Configuration des relances</span>
              </div>
              <div className="p-4 space-y-4">

                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">Mode de relance</div>
                  <div className="space-y-2">
                    {[
                      { id: 'manual', label: 'Manuel', desc: 'Je décide moi-même quand relancer' },
                      { id: 'auto', label: 'Automatique', desc: 'Hellodia relance seule après le délai' },
                      { id: 'both', label: 'Les deux', desc: 'Hellodia signale ET peut relancer' },
                    ].map(opt => (
                      <label key={opt.id} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="relanceType"
                          value={opt.id}
                          checked={relanceType === opt.id}
                          onChange={() => setRelanceType(opt.id)}
                          className="mt-0.5 accent-orange-500"
                        />
                        <div>
                          <div className="text-sm text-gray-800">{opt.label}</div>
                          <div className="text-xs text-gray-400">{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    Délai avant relance : <span className="text-orange-600">{relanceDelay} jours</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={14}
                    value={relanceDelay}
                    onChange={e => setRelanceDelay(Number(e.target.value))}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>1 jour</span>
                    <span>14 jours</span>
                  </div>
                </div>

              </div>
            </div>
          )}

          <button
            onClick={saveMode}
            disabled={saving}
            className="w-full py-3 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : saved ? '✓ Sauvegardé !' : 'Sauvegarder les modifications'}
          </button>
        </div>
      )}

      {/* ── PRODUITS ── */}
      {activeTab === "products" && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-800">
              Catalogue produits ({products.length})
            </span>
            <button className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700">
              <Plus size={14} />
              Ajouter
            </button>
          </div>
          {products.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              Aucun produit configuré
            </div>
          ) : (
            products.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800">{p.name}</div>
                  {p.attributes && Object.keys(p.attributes).length > 0 && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {Object.entries(p.attributes).map(([k, v]) =>
                        `${k}: ${Array.isArray(v) ? v.join('·') : v}`
                      ).join(' · ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-orange-600">
                    {p.price?.toLocaleString()} FCFA
                  </span>
                  <button className="px-2 py-1 text-xs border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
                    Modifier
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "subscription" && (
  <div className="space-y-4">
    {org?.is_trial && (
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
        <div className="text-sm font-medium text-orange-800 mb-1">🎁 Période d'essai gratuite</div>
        <div className="text-xs text-orange-700">
          Vous êtes en période d'essai. Choisissez un plan pour continuer après 24h.
        </div>
      </div>
    )}
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-800">Plans disponibles</span>
      </div>
      <div className="p-4 space-y-4">
        {[
          {
            name: 'Nano',
            prices: { EUR:'19€', MAD:'190 MAD', FCFA:'12 500 FCFA', NGN:'31 000 NGN', GBP:'16£', USD:'21$' },
            clients: '50 clients', msgs: '1 200 messages',
            features: ['Vente'], region: 'Afrique franco. uniquement'
          },
          {
            name: 'Starter',
            prices: { EUR:'59€', MAD:'590 MAD', FCFA:'38 500 FCFA', NGN:'97 000 NGN', GBP:'50£', USD:'64$' },
            clients: '150 clients', msgs: '3 600 messages',
            features: ['Vente', 'Support'], region: 'Tous pays'
          },
          {
            name: 'Pro',
            prices: { EUR:'119€', MAD:'1 190 MAD', FCFA:'78 000 FCFA', NGN:'196 000 NGN', GBP:'101£', USD:'129$' },
            clients: '350 clients', msgs: '8 400 messages',
            features: ['Vente', 'Support', 'Relances'], region: 'Tous pays'
          },
        ].map((plan, i) => (
          <div key={i} className={`border rounded-xl p-4 ${org?.plan_name === plan.name.toLowerCase() ? 'border-orange-300 bg-orange-50' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-medium text-gray-800">{plan.name}</span>
                <span className="text-xs text-gray-400 ml-2">{plan.region}</span>
              </div>
              <span className="text-sm font-medium text-orange-600">
                {plan.prices[org?.currency || 'EUR']}/mois
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {plan.features.map((f, j) => (
                <span key={j} className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full">
                  ✓ {f}
                </span>
              ))}
              <span className="text-xs bg-gray-50 text-gray-500 border border-gray-100 px-2 py-0.5 rounded-full">
                {plan.clients}
              </span>
              <span className="text-xs bg-gray-50 text-gray-500 border border-gray-100 px-2 py-0.5 rounded-full">
                {plan.msgs}
              </span>
            </div>
            <button className="w-full py-2 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 transition-colors">
              {org?.plan_name === plan.name.toLowerCase() ? 'Plan actuel' : `Passer au ${plan.name}`}
            </button>
          </div>
        ))}

        <div className="border border-gray-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-800">Premium</span>
            <span className="text-sm font-medium text-gray-500">Sur devis</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {['Vente', 'Support', 'Relances', 'Volume illimité', 'Multi-users'].map((f, j) => (
              <span key={j} className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full">
                ✓ {f}
              </span>
            ))}
          </div>
          <button className="w-full py-2 border border-orange-600 text-orange-600 rounded-lg text-xs font-medium hover:bg-orange-50 transition-colors">
            Demander un devis
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <button
          onClick={async () => {
            await supabase.auth.signOut();
            window.location.reload();
          }}
          className="w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
        >
          Se déconnecter
        </button>
      </div>

    </div>
  );
}